'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { PublishMode } from '@/features/upload/config/types';
import type { PrecheckFinding } from '@/features/upload/lib/precheck';
import { fileReferenceStore } from './file-reference-store';

/**
 * Upload Phase States
 */
export type UploadPhase =
  'idle' | 'preparing' | 'uploading' | 'reconnecting' | 'processing' | 'complete' | 'error';

/**
 * Circuit Breaker State
 * Prevents infinite retry loops (the "250 ghost videos" disaster)
 */
export interface CircuitBreakerState {
  failureCount: number;
  lastFailureAt: number | null;
  isOpen: boolean; // When open, no retries allowed - requires manual intervention
}

/**
 * Active Upload Entry
 */
export interface ActiveUpload {
  // Identity
  id: string; // Client-generated UUID for tracking
  videoId: number | null;
  videoUuid: string | null;
  bunnyVideoId: string | null;
  /**
   * Idempotency key for the fetch-from-URL path only. Persisted so a retry of
   * the same import reuses it and BE short-circuits to the existing row
   * instead of dispatching a second Bunny fetch. Undefined for TUS uploads.
   */
  fetchClientId?: string;

  // File info
  fileName: string;
  fileSize: number;
  contentType: 'video' | 'short';
  videoWidth?: number;
  videoHeight?: number;
  /**
   * Source duration in seconds, from the same `<video>` metadata probe that
   * yields the dimensions. Encode cost tracks duration × ladder rungs, not file
   * size — a 23 MB clip has taken 632s while a 13.6 GB one took 1333s — so
   * without this no "is this processing time normal?" question is answerable
   * (FRONT-434). Undefined when the probe fails or times out.
   */
  durationSeconds?: number;

  // Progress
  phase: UploadPhase;
  progress: number; // 0-100
  bytesUploaded: number;
  bytesTotal: number;
  encodeProgress?: number; // Bunny encode progress (0-100), updated by poller
  mbpsObserved?: number;
  etaSeconds?: number;
  /**
   * When the upload entered `processing` — i.e. TUS delivered the last byte.
   * The clock the creator is actually watching once the bar hits 100%, and the
   * origin for `ack_gap_ms` and the processing-stall thresholds. Distinct from
   * `startedAt`, which runs from the store entry's creation.
   */
  processingStartedAt?: number;
  /**
   * Network attempts tus has made on the current chunk, from its own counter.
   *
   * Distinct from the history store's `retryCount`, which counts the creator
   * pressing "Retry upload". Both were once shown as "Retries", so a six-attempt
   * network collapse reported as `Retries: 0` and pointed support away from the
   * cause (FRONT-415). tus resets this once a chunk commits, so it reads as
   * "attempts on the chunk we are stuck on", not a lifetime total.
   */
  tusRetryAttempts?: number;

  // TUS resumption
  tusFingerprint: string | null;

  // Metadata (form data collected during upload)
  metadata: {
    title: string;
    description?: string;
    /** Tag IDs for UI selection */
    tags?: number[];
    /** Tag slugs for API payload */
    tagSlugs?: string[];
    isAdultContent?: boolean;
    isSubstanceContent?: boolean;
    isExclusiveContent?: boolean;
    isPublic?: boolean;
    countryId?: number;
    location?: string;
    latitude?: number;
    longitude?: number;
    // Thumbnail
    thumbnailSource: 'auto' | 'custom';
    thumbnailPath?: string;
    // Publishing
    publishMode: PublishMode;
    scheduledAt?: string; // ISO timestamp
  };

  // Circuit breaker for this specific upload
  circuitBreaker: CircuitBreakerState;

  // State
  isStale: boolean; // True for hydrated uploads with no live TUS client
  error: string | null;
  /**
   * Where `error` came from. `'processing'` = Bunny rejected the file after
   * the bytes landed (the poller writes it, `error` is the encoder string);
   * absent = the upload itself failed (TUS / dispatch). Consumers render
   * copy from this, never by sniffing the message text.
   */
  failureStage?: 'processing' | undefined;
  /**
   * Non-blocking file sanity findings (FRONT-348), written once the head
   * sniff + metadata probe settle. Informational only — the upload proceeds
   * regardless; the modal card lists them.
   */
  precheck?: PrecheckFinding[];
  /**
   * The processing poller can't reach the API (network outage after the
   * bytes landed). Bunny keeps encoding — this is not a failure. Cleared on
   * the next successful poll and whenever the entry leaves `processing`.
   */
  pollLost?: boolean | undefined;

  // Modal UI state
  modalStep: 'content' | 'location' | 'review';
  hasOpenModal: boolean; // True when modal is open for this upload (prevents auto-clear)

  // Timestamps
  startedAt: number;
  lastProgressAt: number;
}

/**
 * Upload Store State Interface
 */
interface UploadState {
  // Active uploads map (supports concurrent uploads)
  uploads: Map<string, ActiveUpload>;

  // Unused — concurrency enforced in use-immediate-upload-v2.ts startBulkUpload (3 slots)
  maxConcurrent: number;

  // Global circuit breaker settings
  maxRetries: number;
  retryDelayMs: number;

  // Hydration flag
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;

  // ============ ACTIONS ============

  /**
   * Add a new upload to the store
   * Returns the generated upload ID
   */
  addUpload: (
    upload: Omit<
      ActiveUpload,
      | 'id'
      | 'startedAt'
      | 'lastProgressAt'
      | 'circuitBreaker'
      | 'isStale'
      | 'modalStep'
      | 'hasOpenModal'
    >
  ) => string;

  /**
   * Find existing non-terminal upload matching file signature
   * Returns upload ID if found, null otherwise
   */
  findExistingUpload: (
    fileName: string,
    fileSize: number,
    contentType: 'video' | 'short'
  ) => string | null;

  /**
   * Add upload or return existing one with same signature
   * Returns { id, isExisting } to indicate if deduplication occurred
   */
  addOrFindUpload: (
    upload: Omit<
      ActiveUpload,
      | 'id'
      | 'startedAt'
      | 'lastProgressAt'
      | 'circuitBreaker'
      | 'isStale'
      | 'modalStep'
      | 'hasOpenModal'
    >
  ) => { id: string; isExisting: boolean };

  /**
   * Update an existing upload
   */
  updateUpload: (id: string, updates: Partial<ActiveUpload>) => void;

  /**
   * Update upload progress (optimized for frequent calls)
   */
  updateProgress: (id: string, progress: number, bytesUploaded: number) => void;

  /**
   * Update measured throughput (Mbps + ETA seconds). Throttled at the
   * caller via the upload throughput observer.
   */
  updateThroughput: (id: string, update: { mbpsObserved?: number; etaSeconds?: number }) => void;

  /**
   * Set upload phase with optional error
   */
  setPhase: (id: string, phase: UploadPhase, error?: string) => void;

  /**
   * Update upload metadata
   */
  updateMetadata: (id: string, metadata: Partial<ActiveUpload['metadata']>) => void;

  /**
   * Update modal step for an upload
   */
  updateModalStep: (id: string, step: ActiveUpload['modalStep']) => void;

  /**
   * Set whether modal is open for an upload (prevents auto-clear)
   */
  setModalOpen: (id: string, isOpen: boolean) => void;

  /**
   * Clear error for an upload (without changing phase)
   */
  clearError: (id: string) => void;

  /**
   * Remove an upload from the store
   */
  removeUpload: (id: string) => void;

  /**
   * Clear all completed/errored uploads
   */
  clearCompleted: () => void;

  /**
   * Clean up orphaned uploads (older than 24h)
   */
  cleanupOrphaned: () => void;

  /**
   * Mark hydrated uploads as stale (no live TUS client)
   * Called during rehydration for uploads that were in progress
   */
  markHydratedUploadsStale: () => void;

  // ============ CIRCUIT BREAKER ACTIONS ============

  /**
   * Record a failure for an upload's circuit breaker
   * Returns true if retry is allowed, false if circuit is now open
   */
  recordFailure: (id: string, error: string) => boolean;

  /**
   * Reset circuit breaker for an upload (manual retry)
   */
  resetCircuitBreaker: (id: string) => void;

  /**
   * Check if retry is allowed for an upload
   */
  canRetry: (id: string) => boolean;

  // ============ SELECTORS ============

  /**
   * Get a specific upload
   */
  getUpload: (id: string) => ActiveUpload | undefined;

  /**
   * Get all active uploads as array
   */
  getActiveUploads: () => ActiveUpload[];

  /**
   * Get count of currently uploading items
   */
  getUploadingCount: () => number;

  /**
   * Get validated link for an upload
   * Returns /shorts/[uuid] or /video/[uuid] based on contentType
   */
  getUploadLink: (id: string) => string | null;

  /**
   * Check if any uploads are in progress
   */
  hasActiveUploads: () => boolean;
}

/**
 * Initial circuit breaker state
 */
const initialCircuitBreaker: CircuitBreakerState = {
  failureCount: 0,
  lastFailureAt: null,
  isOpen: false,
};

/**
 * Maximum age for orphaned upload cleanup (24 hours)
 */
const ORPHAN_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/**
 * Upload Store
 *
 * Global state for managing video uploads with:
 * - localStorage persistence (survives page reload)
 * - Map serialization (supports concurrent uploads)
 * - Circuit breaker pattern (prevents infinite retry loops)
 */
export const useUploadStore = create<UploadState>()(
  persist(
    (set, get) => ({
      uploads: new Map(),
      // Unused — concurrency enforced in use-immediate-upload-v2.ts startBulkUpload (3 slots)
      maxConcurrent: 2,
      maxRetries: 3,
      retryDelayMs: 3000,
      _hasHydrated: false,

      setHasHydrated: (state) => set({ _hasHydrated: state }),

      // ============ ACTIONS ============

      addUpload: (upload) => {
        const id = crypto.randomUUID();
        const now = Date.now();

        set((state) => {
          const newUploads = new Map(state.uploads);
          newUploads.set(id, {
            ...upload,
            id,
            startedAt: now,
            lastProgressAt: now,
            circuitBreaker: { ...initialCircuitBreaker },
            isStale: false,
            modalStep: 'content', // Initialize modal step
            hasOpenModal: false,
          });
          return { uploads: newUploads };
        });

        return id;
      },

      findExistingUpload: (fileName, fileSize, contentType) => {
        const uploads = get().uploads;
        for (const [id, upload] of uploads) {
          // Match file signature
          if (
            upload.fileName === fileName &&
            upload.fileSize === fileSize &&
            upload.contentType === contentType &&
            // Non-terminal: not complete and not error
            upload.phase !== 'complete' &&
            upload.phase !== 'error'
          ) {
            return id;
          }
        }
        return null;
      },

      addOrFindUpload: (upload) => {
        // Check for existing upload with same signature
        const existingId = get().findExistingUpload(
          upload.fileName,
          upload.fileSize,
          upload.contentType
        );

        if (existingId) {
          return { id: existingId, isExisting: true };
        }

        // Create new upload
        const id = get().addUpload(upload);
        return { id, isExisting: false };
      },

      updateUpload: (id, updates) => {
        set((state) => {
          const upload = state.uploads.get(id);
          if (!upload) return state;

          const newUploads = new Map(state.uploads);
          const next = { ...upload, ...updates, lastProgressAt: Date.now() };
          // Leaving the error phase (retry / resume) drops the stage with it.
          if (next.phase !== 'error') next.failureStage = undefined;
          if (next.phase !== 'processing') next.pollLost = undefined;
          newUploads.set(id, next);
          return { uploads: newUploads };
        });
      },

      updateProgress: (id, progress, bytesUploaded) => {
        const upload = get().uploads.get(id);
        if (!upload) return;

        // Early return if values unchanged (progress updates are frequent)
        if (upload.progress === progress && upload.bytesUploaded === bytesUploaded) return;

        // Throttle: bytesUploaded changes on every XHR progress event (~20/s),
        // and each set() re-renders every subscriber. Unthrottled, that render
        // rate cascades into React #185 during active uploads. Write only when
        // the visible percent changes, the upload completes, or 250ms elapsed
        // (keeps lastProgressAt fresh for the stale detector).
        const progressChanged = upload.progress !== progress;
        const isTerminal = progress === 100;
        const throttleElapsed = Date.now() - upload.lastProgressAt >= 250;
        if (!progressChanged && !isTerminal && !throttleElapsed) return;

        set((state) => {
          const upload = state.uploads.get(id);
          if (!upload) return state;

          const newUploads = new Map(state.uploads);
          newUploads.set(id, {
            ...upload,
            progress,
            bytesUploaded,
            lastProgressAt: Date.now(),
          });
          return { uploads: newUploads };
        });
      },

      updateThroughput: (id, update) => {
        set((state) => {
          const upload = state.uploads.get(id);
          if (!upload) return state;

          const newUploads = new Map(state.uploads);
          newUploads.set(id, {
            ...upload,
            ...(update.mbpsObserved !== undefined ? { mbpsObserved: update.mbpsObserved } : {}),
            ...(update.etaSeconds !== undefined ? { etaSeconds: update.etaSeconds } : {}),
          });
          return { uploads: newUploads };
        });
      },

      setPhase: (id, phase, error) => {
        set((state) => {
          const upload = state.uploads.get(id);
          if (!upload) return state;

          const newUploads = new Map(state.uploads);
          newUploads.set(id, {
            ...upload,
            phase,
            error: error ?? null,
            ...(phase === 'error' ? {} : { failureStage: undefined }),
            ...(phase === 'processing' ? {} : { pollLost: undefined }),
            // Stamped on the transition INTO processing only, so a re-entry
            // (poll-lost recovery flipping phase around) can't restart the
            // clock and hide a stall that has been running for an hour.
            ...(phase === 'processing' && upload.phase !== 'processing'
              ? { processingStartedAt: Date.now() }
              : {}),
            lastProgressAt: Date.now(),
          });
          return { uploads: newUploads };
        });
      },

      updateMetadata: (id, metadata) => {
        const upload = get().uploads.get(id);
        if (!upload) return;

        // Early return if no actual changes (shallow compare)
        const metadataKeys = Object.keys(metadata) as (keyof typeof metadata)[];
        const hasChanges = metadataKeys.some((key) => upload.metadata[key] !== metadata[key]);
        if (!hasChanges) return;

        set((state) => {
          const upload = state.uploads.get(id);
          if (!upload) return state;

          const newUploads = new Map(state.uploads);
          newUploads.set(id, {
            ...upload,
            metadata: { ...upload.metadata, ...metadata },
            lastProgressAt: Date.now(),
          });
          return { uploads: newUploads };
        });
      },

      updateModalStep: (id, step) => {
        set((state) => {
          const upload = state.uploads.get(id);
          if (!upload) return state;

          const newUploads = new Map(state.uploads);
          newUploads.set(id, {
            ...upload,
            modalStep: step,
          });
          return { uploads: newUploads };
        });
      },

      setModalOpen: (id, isOpen) => {
        const upload = get().uploads.get(id);
        // Early return if value unchanged - prevents infinite loops
        if (!upload || upload.hasOpenModal === isOpen) return;

        set((state) => {
          const upload = state.uploads.get(id);
          if (!upload) return state;

          const newUploads = new Map(state.uploads);
          newUploads.set(id, {
            ...upload,
            hasOpenModal: isOpen,
          });
          return { uploads: newUploads };
        });
      },

      clearError: (id) => {
        set((state) => {
          const upload = state.uploads.get(id);
          if (!upload) return state;

          const newUploads = new Map(state.uploads);
          newUploads.set(id, {
            ...upload,
            error: null,
          });
          return { uploads: newUploads };
        });
      },

      removeUpload: (id) => {
        // Clean up file reference before removing from store
        fileReferenceStore.remove(id);

        set((state) => {
          const newUploads = new Map(state.uploads);
          newUploads.delete(id);
          return { uploads: newUploads };
        });
      },

      clearCompleted: () => {
        set((state) => {
          const newUploads = new Map(state.uploads);
          for (const [id, upload] of newUploads) {
            if (upload.phase === 'complete' || upload.phase === 'error') {
              // Clean up file reference before removing
              fileReferenceStore.remove(id);
              newUploads.delete(id);
            }
          }
          return { uploads: newUploads };
        });
      },

      cleanupOrphaned: () => {
        const now = Date.now();
        set((state) => {
          const newUploads = new Map(state.uploads);
          for (const [id, upload] of newUploads) {
            // Remove uploads older than 24h that are stuck
            if (now - upload.lastProgressAt > ORPHAN_THRESHOLD_MS) {
              // Clean up file reference before removing
              fileReferenceStore.remove(id);
              newUploads.delete(id);
            }
          }
          return { uploads: newUploads };
        });
      },

      markHydratedUploadsStale: () => {
        set((state) => {
          const newUploads = new Map(state.uploads);
          let hasChanges = false;

          for (const [id, upload] of newUploads) {
            // Mark as stale if in-progress (no live TUS client after hydration)
            // 'processing' not included — doesn't need TUS client,
            // the processing poller will handle it
            if (upload.phase === 'uploading' || upload.phase === 'preparing') {
              console.warn('[UploadStore] markHydratedUploadsStale — marking stale on hydration:', {
                id,
                phase: upload.phase,
                videoId: upload.videoId,
              });
              newUploads.set(id, { ...upload, isStale: true });
              hasChanges = true;
            }
          }

          return hasChanges ? { uploads: newUploads } : state;
        });
      },

      // ============ CIRCUIT BREAKER ACTIONS ============

      recordFailure: (id, error) => {
        const state = get();
        const upload = state.uploads.get(id);
        if (!upload) return false;

        const newFailureCount = upload.circuitBreaker.failureCount + 1;
        const isOpen = newFailureCount >= state.maxRetries;

        set((s) => {
          const newUploads = new Map(s.uploads);
          const existingUpload = newUploads.get(id);
          if (!existingUpload) return s;

          newUploads.set(id, {
            ...existingUpload,
            circuitBreaker: {
              failureCount: newFailureCount,
              lastFailureAt: Date.now(),
              isOpen,
            },
            // If circuit is open, set phase to error
            ...(isOpen
              ? {
                  phase: 'error' as UploadPhase,
                  error: `Upload failed after ${state.maxRetries} attempts: ${error}. Please retry manually.`,
                }
              : {}),
          });
          return { uploads: newUploads };
        });

        // Return true if retry is still allowed, false if circuit is now open
        return !isOpen;
      },

      resetCircuitBreaker: (id) => {
        set((state) => {
          const upload = state.uploads.get(id);
          if (!upload) return state;

          const newUploads = new Map(state.uploads);
          newUploads.set(id, {
            ...upload,
            circuitBreaker: { ...initialCircuitBreaker },
            error: null,
          });
          return { uploads: newUploads };
        });
      },

      canRetry: (id) => {
        const upload = get().uploads.get(id);
        if (!upload) return false;
        return !upload.circuitBreaker.isOpen;
      },

      // ============ SELECTORS ============

      getUpload: (id) => get().uploads.get(id),

      getActiveUploads: () => Array.from(get().uploads.values()),

      getUploadingCount: () => {
        return Array.from(get().uploads.values()).filter(
          (u) => u.phase === 'uploading' || u.phase === 'preparing'
        ).length;
      },

      getUploadLink: (id) => {
        const upload = get().uploads.get(id);
        if (!upload || !upload.videoUuid) return null;

        // Generate correct URL based on content type
        const basePath = upload.contentType === 'short' ? '/shorts/' : '/video/';
        return `https://taboo.tv${basePath}${upload.videoUuid}`;
      },

      hasActiveUploads: () => {
        return Array.from(get().uploads.values()).some(
          (u) => u.phase === 'uploading' || u.phase === 'preparing' || u.phase === 'processing'
        );
      },
    }),
    {
      name: 'taboo-uploads',
      storage: createJSONStorage(() => localStorage),

      // Serialize Map to array for JSON storage
      partialize: (state) => ({
        uploads: Array.from(state.uploads.entries()),
      }),

      // Deserialize array back to Map on hydration
      merge: (persisted, current) => {
        const persistedState = persisted as {
          uploads?: [string, ActiveUpload][];
        };

        // Migrate legacy step names from 5-step wizard to 3-step wizard
        const LEGACY_STEP_MAP: Record<string, ActiveUpload['modalStep']> = {
          details: 'content',
          tags: 'content',
          'content-rating': 'content',
          publishing: 'review',
          location: 'location',
        };

        const entries = (persistedState.uploads || []).map(([id, upload]) => {
          const step = upload.modalStep as string;
          if (LEGACY_STEP_MAP[step]) {
            upload = { ...upload, modalStep: LEGACY_STEP_MAP[step]! };
          }
          return [id, upload] as [string, ActiveUpload];
        });

        return {
          ...current,
          uploads: new Map(entries),
        };
      },

      // Called when store is rehydrated from localStorage
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasHydrated(true);
          state.cleanupOrphaned();
          state.markHydratedUploadsStale();
        }
      },
    }
  )
);

/**
 * Selector hooks for optimized subscriptions
 * Use these to avoid unnecessary re-renders
 */
export const useActiveUploads = () => useUploadStore((state) => state.getActiveUploads());
export const useUploadingCount = () => useUploadStore((state) => state.getUploadingCount());
export const useHasActiveUploads = () => useUploadStore((state) => state.hasActiveUploads());
export const useUploadById = (id: string) => useUploadStore((state) => state.getUpload(id));
